import { Comment, Typography } from "antd";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useInView } from "react-intersection-observer";

import { useRequiredContext } from "helpers/globalContext/useRequiredContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { BTUser } from "commonComponents/btWrappers/BTUser/BTUser";
import { BTEditorChat } from "commonComponents/btWrappers/editor/BTEditorChat/BTEditorChat";
import { type DiscussionCommentEntity } from "commonComponents/entity/comment/CommentContainer/CommentContainer.api.types";
import SelectedCommentMenu from "commonComponents/entity/comment/SelectedCommentMenu";
import { Reactions } from "commonComponents/entity/reaction/Reactions";
import { DateDisplay } from "commonComponents/utilities/DateDisplay/DateDisplay";
import { TextWrap } from "commonComponents/utilities/TextWrap/TextWrap";

import {
    BTLoginTypes,
    type UserInteractionsContract,
    UserInteractionsEntityType,
    UserInteractionsType,
} from "handlers";

import { useChatEditorConfig } from "entity/chat/common/useChatEditorConfig";

import "./LegacyBTComment.less";

interface IBTCommentProps {
    comment: DiscussionCommentEntity;
    onDeleteComment: (comment: DiscussionCommentEntity) => void;
    showCommentInfo: boolean;
    isInListItem?: boolean;
    onView?: () => void;
    onSendChat?: () => void;
    isUnread?: boolean;
    onCommentReaction: (commentId: number, data: UserInteractionsContract) => Promise<void>;
    reactionData: UserInteractionsContract[];
    isExternal?: boolean;
    isReadOnly?: boolean;
    isCommentSelected?: boolean;
}

const noOpt = () => {};

const readCommentDurationInMilliseconds = moment.duration(0.5, "seconds").asMilliseconds();

export const LegacyBTComment = ({
    comment,
    onDeleteComment,
    showCommentInfo,
    onView,
    isInListItem,
    isUnread,
    onSendChat,
    reactionData,
    onCommentReaction,
    isExternal,
    isReadOnly,
    isCommentSelected,
}: IBTCommentProps) => {
    const userInfo = useRequiredContext(UserInfoContext);
    const viewed = useRef(false);
    const editorConfig = useChatEditorConfig();
    const [inViewRef, inView] = useInView({
        delay: readCommentDurationInMilliseconds,
        threshold: 0.1,
    });
    const anchorRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (inView && !viewed.current) {
            onView?.();
            viewed.current = true;
        }
    }, [inView, onView]);

    const deleteComment = useCallback(() => {
        onDeleteComment(comment);
    }, [comment, onDeleteComment]);

    let className = !comment.byCurrentUser ? "BTComment" : "BTComment BTComment-CurrentUser";

    if (comment.byCurrentUser && !isInListItem) {
        className = `${className} BTComment-CurrentUser-Padding`;
    }

    const datetime = <DateDisplay value={comment.commentDate} showTime={true} />;
    const commentTestId = `comment-${comment.id}-text`;

    const onlyShowReactionTypes = useMemo(
        () =>
            Object.values(UserInteractionsType).filter((t) =>
                reactionData.some(
                    (r) => r.interactionType === t && moment(r.reactDateUtc).isValid(),
                ),
            ),
        [reactionData],
    );

    return (
        <Comment
            data-testid={`comment-${comment.id}`}
            className={className}
            author={
                isUnread ? (
                    <Typography.Text strong>{comment.userFullName}</Typography.Text>
                ) : (
                    comment.userFullName
                )
            }
            avatar={
                <BTUser
                    displayName={comment.userFullName ?? comment.builderName ?? ""}
                    globalUserId={comment.addedById}
                />
            }
            content={
                <>
                    <TextWrap>
                        {comment.commentIsJson ? (
                            <div ref={inViewRef} data-testid={commentTestId}>
                                <BTEditorChat
                                    onBlur={noOpt}
                                    onChange={noOpt}
                                    id={`${commentTestId}_content`}
                                    data-testid={`${commentTestId}_content`}
                                    value={comment.commentJson}
                                    readOnly
                                    disableSubmitOnEnterKey
                                    onSendChat={onSendChat}
                                    extensionConfig={editorConfig}
                                    replyContentClassName="Legacy"
                                />
                            </div>
                        ) : (
                            <p ref={inViewRef} data-testid={commentTestId}>
                                {isUnread ? (
                                    <Typography.Text strong>{comment.commentText}</Typography.Text>
                                ) : (
                                    comment.commentText
                                )}
                            </p>
                        )}
                    </TextWrap>

                    <Reactions
                        builderId={comment.builderId}
                        entityId={comment.id}
                        entityType={UserInteractionsEntityType.Comments}
                        data={reactionData}
                        onlyShowTypes={onlyShowReactionTypes}
                        onReact={async (data) => await onCommentReaction(comment.id, data)}
                        buttonType="secondary"
                        showNames={userInfo.loginType === BTLoginTypes.BUILDER}
                        isReadOnly={isExternal}
                        className="ReactionsDisplay"
                    />
                    <div
                        ref={anchorRef}
                        style={{ position: "absolute", top: -40, right: 0 }}
                        className="FloatAnchor"
                    />
                    {isCommentSelected && !isReadOnly && (
                        <SelectedCommentMenu
                            comment={comment}
                            onDeleteComment={deleteComment}
                            showCommentInfo={showCommentInfo}
                            isListItem={true}
                            onReplyToComment={() => {}}
                            reactionData={reactionData}
                            onCommentReaction={onCommentReaction}
                            isExternal={isExternal}
                            anchorRef={anchorRef}
                        />
                    )}
                </>
            }
            datetime={isUnread ? <Typography.Text strong>{datetime}</Typography.Text> : datetime}
        />
    );
};
